/**
 * §7.19 Cost-per-DALY. Couples the SEIRD engine + NPI basket against
 * @biomax/plugin-economic: simulate with-and-without an intervention,
 * convert the difference into deaths-averted + hospitalisations-averted,
 * build a YLL + YLD bundle, then divide an estimated intervention cost
 * by DALYs averted. Classifies via WHO-CHOICE per-capita-GDP thresholds.
 *
 * Engine plumbing exercised:
 *   - simulateSeird (baseline + with-intervention)
 *   - composeBetaScaler (NPI stacking)
 *   - yearsOfLifeLost / yearsLivedWithDisability / DALYs (plugin-economic)
 *   - classifyCostEffectiveness (WHO-CHOICE per-capita GDP rule of thumb)
 */

import { useMemo, useState } from 'react';
import { findPathogen, listPathogens } from '@biomax/plugin-pathogen-database';
import { findRegion, listRegions } from '@biomax/plugin-region-database';
import { simulateSeird } from '@biomax/plugin-epidemiology';
import { NPI_CATALOG, composeBetaScaler, listNpiIds } from '@biomax/plugin-epi-interventions';
import {
  yearsOfLifeLost, yearsLivedWithDisability, DISABILITY_WEIGHTS,
  summariseCostBenefit,
} from '@biomax/plugin-economic';

export function CostPerDaly() {
  const pathogens = listPathogens();
  const regions = listRegions();
  const npis = listNpiIds();

  const [pathogenId, setPathogenId] = useState<string>('sars-cov-2-wildtype');
  const [regionId, setRegionId] = useState<string>('usa');
  const [activeNpis, setActiveNpis] = useState<string[]>(['mask-universal', 'distance-tier-2']);
  const [perCapitaCostUsd, setPerCapitaCostUsd] = useState<number>(15);
  const [gdpPerCapitaUsd, setGdpPerCapitaUsd] = useState<number>(70000);

  const view = useMemo(() => {
    const pathogen = findPathogen(pathogenId);
    const region = findRegion(regionId);
    if (!pathogen || !region) return null;

    const gamma = 1 / pathogen.infectiousPeriodDays.value;
    const sigma = 1 / pathogen.latentPeriodDays.value;
    const beta = pathogen.R0.value * gamma;
    const ifr = pathogen.ifrByAgeBand[Math.floor(pathogen.ifrByAgeBand.length / 2)]?.ifr ?? 0.005;
    const N = region.population.total;
    const duration = 180;

    const baseline = simulateSeird({
      N, I0: 100, beta, gamma, sigma, ifr,
      durationDays: duration, dtDays: 1,
    });

    const schedule = activeNpis.map(id => ({ npiId: id, startDay: 14, endDay: duration - 14 }));
    const betaScaler = schedule.length > 0 ? composeBetaScaler(schedule, NPI_CATALOG) : undefined;
    const treated = simulateSeird({
      N, I0: 100, beta, gamma, sigma, ifr,
      durationDays: duration, dtDays: 1,
    }, betaScaler ? { betaScaler } : undefined);

    const deathsBase = baseline.compartments.D!.at(-1)!;
    const deathsTreated = treated.compartments.D!.at(-1)!;
    const deathsAverted = Math.max(0, deathsBase - deathsTreated);

    const infectedBase = N - baseline.compartments.S!.at(-1)!;
    const infectedTreated = N - treated.compartments.S!.at(-1)!;
    const infectionsAverted = Math.max(0, infectedBase - infectedTreated);

    // Distribute averted deaths over the catalog age structure (GBD-ref life expectancy).
    const ageStructure = region.population.ageStructure;
    const deathCohorts = ageStructure.map(b => ({
      ageAtDeath: (b.ageBandLow + b.ageBandHigh) / 2,
      count: deathsAverted * b.fraction,
    }));
    const yll = yearsOfLifeLost(deathCohorts);

    // Disability cohorts: model ~5% of averted infections as moderate respiratory illness for 14d,
    // ~1% as hospitalisation for 7d. These are intentionally illustrative — proper region- and
    // age-specific weights land in v0.3 (see plugin-economic README).
    const yld = yearsLivedWithDisability([
      {
        caseCount: infectionsAverted * 0.05,
        disabilityWeight: DISABILITY_WEIGHTS.acuteRespiratoryModerate,
        durationYears: 14 / 365,
      },
      {
        caseCount: infectionsAverted * 0.01,
        disabilityWeight: DISABILITY_WEIGHTS.hospitalisationGeneralWard,
        durationYears: 7 / 365,
      },
    ]);
    const dalysAverted = yll + yld;

    const totalCostUsd = perCapitaCostUsd * N;
    const summary = summariseCostBenefit(
      activeNpis.length ? activeNpis.join(' + ') : 'no intervention',
      dalysAverted, totalCostUsd, gdpPerCapitaUsd,
    );

    return {
      pathogen, region, deathsAverted, infectionsAverted,
      yll, yld, dalysAverted, summary, totalCostUsd,
      deathsBase, deathsTreated,
    };
  }, [pathogenId, regionId, activeNpis, perCapitaCostUsd, gdpPerCapitaUsd]);

  if (!view) return <div>Loading…</div>;

  const toggleNpi = (id: string) => {
    setActiveNpis(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const categoryColor =
    view.summary.category === 'highly-cost-effective' ? 'var(--bm-accent)'
    : view.summary.category === 'cost-effective' ? 'var(--bm-signal)'
    : 'var(--bm-danger)';

  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">§7.19</span>
        <h1 className="surface__title">Cost-per-DALY-averted</h1>
      </div>
      <div className="surface__sub">
        SEIRD counterfactual × GBD-2019 DALY accounting × WHO-CHOICE threshold ·
        {' '}{view.region.names.english} · {view.pathogen.names.common}
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">Scenario + cost assumptions</span></div>
        <div className="simctl">
          <label className="simctl__field">
            <span className="simctl__label">Pathogen</span>
            <select value={pathogenId} onChange={e => setPathogenId(e.target.value)}>
              {pathogens.map(p => <option key={p.id} value={p.id}>{p.names.common}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Region</span>
            <select value={regionId} onChange={e => setRegionId(e.target.value)}>
              {regions
                .slice()
                .sort((a, b) => a.names.english.localeCompare(b.names.english))
                .map(r => <option key={r.id} value={r.id}>{r.names.english}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Intervention cost · USD per capita</span>
            <input type="number" value={perCapitaCostUsd}
              onChange={e => setPerCapitaCostUsd(parseFloat(e.target.value) || 0)} />
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Region GDP/capita · USD</span>
            <input type="number" value={gdpPerCapitaUsd}
              onChange={e => setGdpPerCapitaUsd(parseFloat(e.target.value) || 1)} />
          </label>
        </div>
        <div style={{ marginTop: 12 }}>
          <span className="simctl__label">NPI basket · click to toggle</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--bm-space-2)', marginTop: 8 }}>
            {npis.map(id => {
              const active = activeNpis.includes(id);
              return (
                <button
                  key={id}
                  className={active ? 'btn btn--primary' : 'btn'}
                  onClick={() => toggleNpi(id)}
                >
                  {NPI_CATALOG[id]!.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="kpis">
        <div className="kpi">
          <div className="kpi__label">Cost / DALY averted</div>
          <div className="kpi__value" style={{ color: categoryColor }}>
            {Number.isFinite(view.summary.costPerDalyAverted)
              ? `$${Math.round(view.summary.costPerDalyAverted).toLocaleString()}`
              : '∞'}
          </div>
          <div className="kpi__sub">{view.summary.category.replace(/-/g, ' ')}</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">DALYs averted</div>
          <div className="kpi__value kpi__value--accent">{Math.round(view.dalysAverted).toLocaleString()}</div>
          <div className="kpi__sub">YLL {Math.round(view.yll).toLocaleString()} + YLD {Math.round(view.yld).toLocaleString()}</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Deaths averted</div>
          <div className="kpi__value kpi__value--signal">{Math.round(view.deathsAverted).toLocaleString()}</div>
          <div className="kpi__sub">{Math.round(view.deathsBase).toLocaleString()} base → {Math.round(view.deathsTreated).toLocaleString()} treated</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Infections averted</div>
          <div className="kpi__value">{(view.infectionsAverted / 1e6).toFixed(1)}M</div>
          <div className="kpi__sub">used for YLD computation</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Total intervention cost</div>
          <div className="kpi__value">${(view.totalCostUsd / 1e9).toFixed(2)}B</div>
          <div className="kpi__sub">${perCapitaCostUsd} × {(view.region.population.total / 1e6).toFixed(0)}M people</div>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">WHO-CHOICE classification</span>
        </div>
        <table className="dtable">
          <thead>
            <tr>
              <th>Threshold</th>
              <th className="num">Cost / DALY</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Highly cost-effective</td><td className="num">&lt; ${gdpPerCapitaUsd.toLocaleString()}</td>
              <td style={{ color: view.summary.category === 'highly-cost-effective' ? 'var(--bm-accent)' : 'var(--bm-text-muted)' }}>
                {view.summary.category === 'highly-cost-effective' ? '✓ this basket' : '—'}
              </td></tr>
            <tr><td>Cost-effective</td><td className="num">&lt; ${(gdpPerCapitaUsd * 3).toLocaleString()}</td>
              <td style={{ color: view.summary.category === 'cost-effective' ? 'var(--bm-signal)' : 'var(--bm-text-muted)' }}>
                {view.summary.category === 'cost-effective' ? '✓ this basket' : '—'}
              </td></tr>
            <tr><td>Not cost-effective</td><td className="num">≥ ${(gdpPerCapitaUsd * 3).toLocaleString()}</td>
              <td style={{ color: view.summary.category === 'not-cost-effective' ? 'var(--bm-danger)' : 'var(--bm-text-muted)' }}>
                {view.summary.category === 'not-cost-effective' ? '✗ this basket' : '—'}
              </td></tr>
          </tbody>
        </table>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Method + caveats</span>
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--bm-text)' }}>
          YLL distributes averted deaths over the region's age structure using the GBD-2019
          standard reference life expectancy. YLD uses GBD-2019 disability weights for moderate
          acute respiratory illness (5% of averted infections × 14 days) + general-ward
          hospitalisation (1% × 7 days). These are <strong>illustrative defaults</strong>; v0.3
          will wire region- and pathogen-specific weights from the catalog. WHO-CHOICE 2014
          thresholds at 1×/3× GDP per capita are widely-used heuristics but have been formally
          retired in favour of demand-side opportunity-cost frameworks (Bertram 2016 Bull WHO);
          v0.4 will offer an alternate threshold module.
        </div>
      </div>
    </div>
  );
}
